import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import service from "utils/service";
import useFetchData from "utils/customFetchData";
import { reduxSliceData } from "store/slices/reduxData";

interface CreateCustomerModalProps {
  visible: boolean;
  onClose: () => void;
}

const RechargeWallet: React.FC<CreateCustomerModalProps> = ({
  visible,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const loginCustomer = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("loginCustomer") || "[]");
    } catch {
      return [];
    }
  }, []);
  const [discount, setDiscount] = useState(0);
  const reduxBag = useSelector((state: any) => state.reduxData?.reduxData ?? {});
  const getWalletData = reduxBag?.getWalletData;
  const [balance, setBalance] = useState(0);
  const [loader, setLoader] = useState(false);
  const { addData } = useFetchData();
  const dispatch = useDispatch();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [amount, setAmount] = useState(1500);
  const predefinedAmounts = [500, 1000, 1500, 5000, 10000];
  const customerId = loginCustomer?.[0]?.UserData?.[0]?.CUSTOMER_ID;

  const GATEWAY_PERCENT = 2.5; // 2.5%

  const gatewayCharge = Number(
    ((amount * GATEWAY_PERCENT) / 100).toFixed(2)
  );

  const payableAmount = Number(
    (amount + gatewayCharge).toFixed(2)
  );

  useEffect(() => {
    if (getWalletData) {
      const walletRows = Array.isArray(getWalletData?.data)
        ? getWalletData.data
        : Array.isArray(getWalletData?.apiData)
          ? getWalletData.apiData
          : Array.isArray(getWalletData)
            ? getWalletData
            : [];
      const fetchedBalance = walletRows
        .map((item: any) => Number(item.BALANCE || 0))
        .reduce((acc: number, walletBalance: number) => acc + walletBalance, 0);
      setBalance(fetchedBalance);
    }
  }, [getWalletData]);

  // Load Easebuzz script dynamically
  // Dynamically load the Easebuzz script and track its status
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://mercury.phonepe.com/web/bundle/checkout.js";
    script.async = true;

    // Use an onload event to confirm the script is ready
    script.onload = () => {
      setIsScriptLoaded(true);
    };

    script.onerror = () => {
      enqueueSnackbar("Failed to load PhonePe script.", { variant: "error" });
    };

    document.body.appendChild(script);

    // Clean up the script when the component unmounts
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Handle Easebuzz Payment
  // const handleEasebuzzPayment = useCallback(async () => {
  //   // Check if the script is loaded before proceeding
  //   if (!isScriptLoaded) {
  //     message.error("Payment gateway is not ready yet. Please wait a moment and try again.");
  //     return;
  //   }

  //   try {
  //     setLoader(true);

  //     const body = {
  //       AMOUNT: amount,
  //       FIRST_NAME: loginCustomer[0].UserData[0].NAME,
  //       EMAIL_ID: loginCustomer[0].UserData[0].EMAIL_ID,
  //       MOBILE_NO: loginCustomer[0].UserData[0].MOBILE_NO,
  //       CUSTOMER_ID: [loginCustomer[0].UserData[0].CUSTOMER_ID],
  //     };
  //     console.log("🚀 ~ RechargeWallet ~ body:", body)

  //     const walletData = await addData(body, service.API_URL.wallet_master.update);
  //     console.log("API response with keys:", walletData);

  //     if (walletData?.key && walletData?.access_key) {
  //       // Correctly initialize Easebuzz and handle the options in a single call
  //       const easebuzzCheckout = new (window as any).EasebuzzCheckout(walletData.key, "prod");

  //       const options = {
  //         access_key: walletData.access_key,
  //         onResponse: (response: any) => {
  //           console.log("Payment response:", response);
  //           if (response?.status === "success") {
  //             setShowSuccessModal(true);
  //             // addWalletBalance(response.txnid);
  //             message.success("Payment successful!");
  //           } else {
  //             message.error("Payment failed or cancelled.");
  //           }
  //         },
  //         theme: "#123456",
  //       };

  //       // Initiate the payment with all options in one go
  //       easebuzzCheckout.initiatePayment(options);
  //     } else {
  //       message.error("Invalid payment gateway response.");
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     message.error("Error occurred while initiating payment.");
  //   } finally {
  //     setLoader(false);
  //   }
  // }, [isScriptLoaded, loginCustomer.UserData, amount]);

    const handlePhonepePayment = useCallback(async () => {
    // Check if the script is loaded before proceeding
    if (!isScriptLoaded) {
        enqueueSnackbar("Payment gateway is not ready yet. Please wait a moment and try again.", {
          variant: "error",
        });
      return;
    }

    try {
      setLoader(true);
        const walletMaster = (service as any)?.API_URL?.wallet_master;
        if (!walletMaster?.phonepe || !walletMaster?.phonepeStatus) {
          enqueueSnackbar("Wallet payment API is not configured.", { variant: "error" });
          return;
        }

        if (!customerId) {
          enqueueSnackbar('Customer profile is missing.', { variant: 'error' });
          return;
        }

        let merchantId: string | number | null = null;
      const body = {
        amount: payableAmount,
        custId: customerId,
      };

        const walletData = await addData(body, walletMaster.phonepe);
        const responseData = walletData?.apiData ?? walletData?.data ?? walletData;

      if ((walletData?.code == 200 || walletData?.statusCode === 200 || walletData?.success === true) && (responseData?.redirectUrl || responseData?.tokenUrl)) {
        
        merchantId = responseData?.merchantOrderId ?? responseData?.merchantOrderID ?? responseData?.merchantOrderId ?? null;
          const callback = async (response: string) => {
          if (response === 'USER_CANCEL') {
              await addData(
                { merchantOrderId: merchantId, status: "P", custId: customerId },
                walletMaster.phonepeStatus,
              );
            return;
          } else if (response === 'CONCLUDED') {
              await addData(
                { merchantOrderId: merchantId, status: "C", custId: customerId },
                walletMaster.phonepeStatus,
              );
              dispatch(reduxSliceData({ key: "paymentSuccess", data: true }));
              setShowSuccessModal(true);
            return;
          }
          };

        // Initiate the payment with all options in one go
          (window as any).PhonePeCheckout.transact({
            tokenUrl: responseData?.redirectUrl ?? responseData?.tokenUrl,
            callback,
            type: "IFRAME",
          });
      } else {
          enqueueSnackbar("Invalid payment gateway response.", { variant: "error" });
      }
    } catch (error) {
        enqueueSnackbar("Error occurred while initiating payment.", { variant: "error" });
    } finally {
      setLoader(false);
    }
    }, [addData, amount, dispatch, enqueueSnackbar, isScriptLoaded, loginCustomer, payableAmount]);


  // Add Wallet Balance after payment success
  const addWalletBalance = async (transactionId?: any) => {
    setLoader(true);
    const body = {
      CUSTOMER_ID: customerId,
      AMOUNT: amount,
      TRANSACTION_ID: transactionId,
    };
    const walletData = await addData(
      body,
      (service as any)?.API_URL?.wallet_master?.add
    );
    if (walletData.code === 200) {
      setLoader(false);
      dispatch(reduxSliceData({ key: "paymentSuccess", data: true }));
    }
  };

  const handleFormSubmit = useCallback(() => {
    onClose();
    // handleEasebuzzPayment();
    handlePhonepePayment();
  }, [handlePhonepePayment, onClose]);

  return (
    <>
      <Dialog open={visible} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 7 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Recharge Your Wallet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Current Wallet Amt: ₹{balance.toFixed(2)}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="close wallet dialog">
              <IconX stroke={1.5} size="18px" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5} component="form" onSubmit={(event) => { event.preventDefault(); void handleFormSubmit(); }}>
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.light', bgcolor: 'primary.50' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Enter amount in multiples of 100
              </Typography>

              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={(event) => setAmount(Number(event.target.value || 0))}
                inputProps={{ min: 250, max: 50000 }}
                helperText="Min value: ₹250 & Max Value: ₹50,000"
                fullWidth
              />

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                {predefinedAmounts.map((presetAmount) => (
                  <Chip
                    key={presetAmount}
                    label={`₹ ${presetAmount}`}
                    variant={amount === presetAmount ? 'filled' : 'outlined'}
                    color="primary"
                    onClick={() => setAmount(presetAmount)}
                    clickable
                  />
                ))}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Recharge Amount</Typography>
                  <Typography variant="body2">₹ {amount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Coupon Discount</Typography>
                  <Typography variant="body2">₹ {discount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Payment Gateway Charges (2.5%)</Typography>
                  <Typography variant="body2">₹ {gatewayCharge}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Payable Amount</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>₹ {payableAmount}</Typography>
                </Stack>
              </Stack>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loader}
                sx={{ minWidth: 220, borderRadius: 2 }}
              >
                {loader ? 'Processing...' : 'Continue to Payment'}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onClose();
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Payment Successful</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Your payment was successful and the amount will be added to your wallet shortly.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RechargeWallet;
